import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { Badge, Card, ListRow } from '../../ui';
import type { BadgeTone } from '../../ui';
import { listCoachNotes } from '../../shared/queries';
import type { CoachNoteRow } from '../../shared/queries';
import { supabase } from '../../shared/supabase/client';
import { formatMoment } from '../../shared/formatDate';
import { useInstance } from '../instance/InstanceProvider';
import { text } from '../../shared/text';
import type { MessageId } from '../../shared/text';
import { ClientGuard } from './ClientGuard';
import { AppBar, ClientLoading, ClientNotFound, ClientPage } from './ClientPage';
import { ChevronRight } from './icons';
import { markNoteRead, usePresence } from './presence';

/**
 * What her coach has written for her, and the reading of one.
 *
 * ## What a note is
 *
 * A published recommendation: one of exactly three kinds, a title, a body, a
 * date, and — on every one of them, always — who wrote it and the line that says
 * what it is not. There is no medical or clinical kind and there must never be
 * one (`mvp-discovery` → `recommendations`).
 *
 * ## Read-only, and that is the whole feature
 *
 * A client reads. They do not comment, react or reply here. Replying belongs in
 * the thread, where the boundaries are stated and kept; two places to write to
 * the coach would be two places to maintain them (`client-coach-presence`
 * design, D5).
 *
 * ## What is hollow here, said out loud
 *
 * **There is no `recommendation` table.** `listCoachNotes` answers with nothing
 * in any build served by a real database, so what a real client sees today is
 * the empty state at the bottom of this file — which is the truth, not a
 * placeholder. The notes in the demonstration come from
 * `src/shared/fixtures/data.ts`, and whether one has been read is a fact about
 * the browser rather than about the account: see `./presence.ts`. So a client
 * who opens a note on their phone and then opens the app on a laptop finds it
 * unread again, and the coach cannot see that it landed at all. Both stop being
 * true on the day the table exists, and neither before.
 */
export function NotesPage() {
  return (
    <ClientGuard>
      <Notes />
    </ClientGuard>
  );
}

/** The three kinds, their words and their colour. No fourth is representable. */
const KIND_TEXT = {
  training: 'client.notes.kindTraining',
  nutrition: 'client.notes.kindNutrition',
  recovery: 'client.notes.kindRecovery',
} as const satisfies Record<CoachNoteRow['kind'], MessageId>;

const KIND_TONE = {
  training: 'training',
  nutrition: 'nutrition',
  recovery: 'recovery',
} as const satisfies Record<CoachNoteRow['kind'], BadgeTone>;

function useNotes() {
  return useQuery({ queryKey: ['coach-notes'], queryFn: () => listCoachNotes(supabase) });
}

function Notes() {
  const instance = useInstance();
  const notes = useNotes();
  const presence = usePresence();

  const list = notes.data ?? [];

  return (
    <ClientPage>
      <AppBar backTo={`/c/${instance.slug}/today`} backLabel={text('client.notes.back')} />

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">
        {text('client.notes.title')}
      </h1>
      {/*
        Whose words these are, on the list as well as on each note. A client must
        never have to open something to find out who wrote it, and for a coach
        who is also a medical student that is not a presentational concern.
      */}
      <p className="text-ink-muted mt-1 text-sm">
        {text('client.presence.notesMeta', { coach: instance.coachDisplayName })}
      </p>

      {notes.isPending ? (
        <p className="text-ink-muted mt-4 text-sm">{text('app.loading')}</p>
      ) : null}

      {/*
        Nothing written yet. An ordinary card with an ordinary sentence: it is
        not an error, it asks nothing of the client, and it does not suggest that
        anything is late.
      */}
      {!notes.isPending && list.length === 0 ? (
        <Card className="mt-4">
          <h2 className="text-ink font-display text-xl">{text('client.notes.emptyTitle')}</h2>
          <p className="text-ink-soft mt-2 text-sm leading-relaxed">
            {text('client.notes.emptyBody', { coach: instance.coachDisplayName })}
          </p>
        </Card>
      ) : null}

      {list.length === 0 ? null : (
        <ul className="mt-4">
          {list.map((note) => {
            const unread = !presence.readNotes.includes(note.id);
            return (
              <li key={note.id}>
                <Link
                  to={`/c/${instance.slug}/notes/${note.id}`}
                  className="block rounded-[--radius-control]"
                >
                  <ListRow
                    title={note.title}
                    meta={`${text(KIND_TEXT[note.kind])} · ${formatMoment(note.published_at)}`}
                    {...(unread
                      ? {
                          /*
                            Unread is carried by a word on an amber ground, never
                            by a dot and never by colour alone — amber means a
                            person needs attention, and a state only a colour
                            says is a state some readers do not have.
                          */
                          trailing: (
                            <Badge tone="attention">{text('client.presence.unread')}</Badge>
                          ),
                        }
                      : { trailing: <ChevronRight size={20} className="text-ink-muted" /> })}
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </ClientPage>
  );
}

export function NotePage() {
  return (
    <ClientGuard>
      <Note />
    </ClientGuard>
  );
}

/**
 * One note, in full.
 *
 * Opening it marks it read — without asking the client to do anything, which is
 * what `recommendations` → "Read state" requires. The effect runs after the
 * paint, so the list's unread marker is still correct on the way in and correct
 * again on the way back.
 */
function Note() {
  const instance = useInstance();
  const params = useParams();
  const noteId = params['noteId'] ?? '';
  const notes = useNotes();

  const note = notes.data?.find((row) => row.id === noteId) ?? null;

  useEffect(() => {
    if (note !== null) markNoteRead(note.id);
  }, [note]);

  if (notes.isPending) return <ClientLoading />;
  /*
   * A note belonging to another client, a withdrawn one and one that never
   * existed must be indistinguishable — the same single answer every other
   * client screen gives (`access-and-consent`, "no hint that the content
   * exists").
   */
  if (note === null) return <ClientNotFound />;

  return (
    <ClientPage>
      <AppBar backTo={`/c/${instance.slug}/notes`} backLabel={text('client.notes.readerBack')} />

      <div className="mt-2">
        <Badge tone={KIND_TONE[note.kind]}>{text(KIND_TEXT[note.kind])}</Badge>
      </div>

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">{note.title}</h1>

      {/*
        Who wrote it and when, on every note. Nothing a client reads in this
        product is anonymous or appears to come from the product itself, and the
        coach is named by the role the instance row gives her — never by a title
        and never by a literal in this file.
      */}
      <p className="text-ink-soft mt-2 text-sm">
        {text('client.notes.from', {
          coach: instance.coachDisplayName,
          role: instance.roleLine,
        })}
      </p>
      <p className="text-ink-muted mt-0.5 text-xs">
        {text('client.notes.published', { date: formatMoment(note.published_at) })}
      </p>

      <Card className="mt-4">
        {/*
          Her words, as she wrote them. Paragraphs are kept because she typed
          them; nothing else is interpreted, and nothing is rendered as markup.
        */}
        {note.body.split('\n\n').map((paragraph, index) => (
          <p
            key={`${String(index)}-${paragraph.slice(0, 12)}`}
            className={['text-ink text-sm leading-relaxed', index === 0 ? '' : 'mt-3'].join(' ')}
          >
            {paragraph}
          </p>
        ))}
      </Card>

      {/*
        The boundary line. On every note, built from her configured name and role
        rather than written as fixed text, and not something any screen may omit
        (`recommendations`, "Authorship, date and boundary line").
      */}
      <p className="text-ink-muted mt-4 text-xs leading-relaxed">
        {text('client.notes.boundary', {
          coach: instance.coachDisplayName,
          role: instance.roleLine,
        })}
      </p>
    </ClientPage>
  );
}
